/**
 * Socket connection lifecycle management.
 *
 * Handles: connect, disconnect, reconnect, auth, single-flight guard,
 * staleness detection, session recovery, and tab visibility recovery.
 */
import { useEffect, useState, useCallback, useRef } from 'react';
import { reduxLogger, socketLogger } from '@/core/utils/logger';
import { Socket } from 'socket.io-client';
import { SocketClient } from '../../socket/SocketClient';
import { ServerEvents, ClientEvents } from '@voley/shared-types/socket';
import { useSecureAuthContext } from '../../auth/services';
import { wsAuthService } from '../../auth/services/WebSocketAuthService';
import { isSessionInvalidated, markSessionInvalidated, resetSessionInvalidated } from '@/api/core/baseQueryWithRetry';
import { useCurrentUser } from '../../auth/hooks/useCurrentUser';
import { store } from '../../store';
import { recoverSessionState } from '@/core/auth/services/sessionRecovery';
import { attachWindowAttentionListeners } from '@/core/utils/windowAttention';
import { getErrorMessage } from '@voley/shared-utils';
import { baseApi } from '@/api/core/baseApi';

// Type for stored event handlers used for cleanup
interface StoredEventHandler {
  event: string;
  handler: (...args: unknown[]) => void;
}

// Type for auth context tracking
interface AuthContext {
  isAuthenticated: boolean;
  orgId: string | null;
}

export interface SocketConnectionState {
  socket: Socket | null;
  connected: boolean;
  connecting: boolean;
  socketStable: boolean;
  socketClient: SocketClient;
  registerHandler: (socketInstance: Socket, event: string, handler: (...args: unknown[]) => void) => void;
}

export function useSocketConnection(): SocketConnectionState {
  const { isAuthenticated } = useSecureAuthContext();
  const { user, apiUserId } = useCurrentUser();
  const [socket, setSocket] = useState<Socket | null>(null);
  const [connected, setConnected] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [socketStable, setSocketStable] = useState(false);
  const [socketClient] = useState(() => SocketClient.getInstance());
  const currentOrgIdRef = useRef<string | null>(null);

  const connectSocketRef = useRef<(() => Promise<Socket | null>) | null>(null);
  const connectionPromiseRef = useRef<Promise<Socket | null> | null>(null);
  const connectionVersionRef = useRef(0);
  const isMountedRef = useRef(true);

  const currentAuthContextRef = useRef<AuthContext>({
    isAuthenticated: false,
    orgId: null
  });

  const storedHandlersRef = useRef<StoredEventHandler[]>([]);

  const connectedRef = useRef(false);
  const connectingRef = useRef(false);
  const socketRef = useRef<Socket | null>(null);

  useEffect(() => {
    connectedRef.current = connected;
    connectingRef.current = connecting;
    socketRef.current = socket;
  }, [connected, connecting, socket]);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const hasConnectedOnceRef = useRef(false);

  // Debounced socket stability — syncs external socket state to React state
  useEffect(() => {
    if (!connected) {
      setSocketStable(false); // eslint-disable-line react-hooks/set-state-in-effect -- syncs external socket connection state
      return;
    }
    const timer = setTimeout(() => {
      if (isMountedRef.current) {
        setSocketStable(true);
      }
    }, 2000);
    return () => clearTimeout(timer);
  }, [connected]);

  // Reconnect resync
  useEffect(() => {
    if (!socketStable) return;

    if (hasConnectedOnceRef.current) {
      reduxLogger.info('[SocketProvider] Reconnect resync: invalidating hot-path tags');
      const tags: Parameters<typeof baseApi.util.invalidateTags>[0] = [
        { type: 'Conversations', id: 'LIST' },
        { type: 'Conversations', id: 'INFINITE-LIST' },
        { type: 'Notifications', id: 'LIST' },
        'NotificationCount',
        { type: 'TeamConversations', id: 'LIST' },
      ];
      const activeConvId = store.getState().messagingUi?.currentConversationId;
      if (activeConvId) {
        tags.push({ type: 'Messages', id: activeConvId });
      }
      store.dispatch(baseApi.util.invalidateTags(tags));
    }

    hasConnectedOnceRef.current = true;
  }, [socketStable]);

  const registerHandler = useCallback((
    socketInstance: Socket,
    event: string,
    handler: (...args: unknown[]) => void
  ) => {
    socketInstance.on(event, handler);
    storedHandlersRef.current.push({ event, handler });
  }, []);

  const cleanupAllHandlers = useCallback((socketInstance: Socket | null) => {
    if (!socketInstance) return;
    storedHandlersRef.current.forEach(({ event, handler }) => {
      try { socketInstance.off(event, handler); } catch { /* ignore */ }
    });
    storedHandlersRef.current = [];
    try {
      socketInstance.removeAllListeners();
      if (typeof socketInstance.offAny === 'function') {
        socketInstance.offAny();
      }
    } catch { /* ignore */ }
  }, []);

  const connectSocket = useCallback(async (): Promise<Socket | null> => {
    const orgId = user?.organizationId;
    const authContext: AuthContext = {
      isAuthenticated,
      orgId: typeof orgId === 'string' ? orgId : null
    };

    if (!authContext.isAuthenticated) {
      socketLogger.info('[SocketProvider] Not authenticated, skipping socket connection');
      return null;
    }

    if (connectionPromiseRef.current !== null) {
      reduxLogger.info('[SocketProvider] Connection already in progress, awaiting existing promise');
      return connectionPromiseRef.current;
    }

    if (connectedRef.current && socketRef.current?.connected) {
      reduxLogger.info('[SocketProvider] Already connected, returning existing socket');
      return socketRef.current;
    }

    storedHandlersRef.current = [];
    const thisConnectionVersion = ++connectionVersionRef.current;
    currentAuthContextRef.current = authContext;

    const isStale = () => {
      if (connectionVersionRef.current !== thisConnectionVersion) return true;
      if (!isMountedRef.current) return true;
      const currentAuth = currentAuthContextRef.current;
      if (currentAuth.isAuthenticated !== authContext.isAuthenticated ||
          currentAuth.orgId !== authContext.orgId) return true;
      return false;
    };

    const connectionPromise = (async (): Promise<Socket | null> => {
      let token: string | null = null;
      try {
        reduxLogger.info('[SocketProvider] Getting WebSocket JWT token from auth service...');
        token = await wsAuthService.getToken();
        if (!token) throw new Error('WebSocket auth service returned null token');
      } catch (error) {
        reduxLogger.error('[SocketProvider] Failed to get WebSocket JWT token:', error);
        return null;
      }

      if (isStale()) return null;

      socketLogger.info('[SocketProvider] Attempting to connect to socket...');
      if (isMountedRef.current) setConnecting(true);

      try {
        const socketInstance = await socketClient.connect();

        if (isStale()) {
          if (socketInstance) {
            try { socketInstance.disconnect(); } catch { /* ignore */ }
          }
          return null;
        }

        if (socketInstance) {
          if (isMountedRef.current) {
            setSocket(socketInstance);
            setConnected(true);
          }
          reduxLogger.info('[SocketProvider] Socket connected successfully');

          store.dispatch({
            type: 'socket/connected',
            payload: { socketId: socketInstance.id, connected: true }
          });

          // Core connection lifecycle handlers
          const handleConnect = () => {
            reduxLogger.info('[SocketProvider] Socket connected - ID:', socketInstance.id);
            if (isMountedRef.current) { setConnected(true); setConnecting(false); }
            socketInstance.emit(ClientEvents.PING, { timestamp: Date.now() });
            if (currentOrgIdRef.current) {
              reduxLogger.info('[SocketProvider] Rejoining org room after connect:', currentOrgIdRef.current);
              socketClient.joinOrganizationRoom(currentOrgIdRef.current);
              try { socketInstance.emit(ClientEvents.JOIN_ROOM, { room: `organization:${currentOrgIdRef.current}` }); } catch {}
            }
          };

          const handleDisconnect = (reason: string) => {
            reduxLogger.warn('[SocketProvider] Socket disconnected:', reason);
            if (isMountedRef.current) setConnected(false);
          };

          const handleConnectError = (rawError: unknown) => {
            const error: { type?: string; message?: string } =
              typeof rawError === 'object' && rawError !== null
                ? rawError as { type?: string; message?: string }
                : { message: String(rawError) };
            const errorMessage = error.message ?? '';
            reduxLogger.error('[SocketProvider] Socket connection error:', error);
            if (isMountedRef.current) { setConnected(false); setConnecting(false); }
            if (error.type === 'UnauthorizedError' ||
                errorMessage.includes('unauthorized') ||
                errorMessage.includes('jwt expired')) {
              wsAuthService.clearToken();
            }
          };

          const handleReconnect = (attemptNumber: number) => {
            reduxLogger.info('[SocketProvider] Socket reconnected after', attemptNumber, 'attempts');
            if (isMountedRef.current) { setConnected(true); setConnecting(false); }
          };

          const handleError = (rawError: unknown) => {
            const error: { type?: string; message?: string } =
              typeof rawError === 'object' && rawError !== null
                ? rawError as { type?: string; message?: string }
                : { message: String(rawError) };
            const errorMessage = error.message ?? '';
            reduxLogger.error('[SocketProvider] Socket runtime error:', error);
            if (error.type === 'UnauthorizedError' ||
                errorMessage.includes('unauthorized') ||
                errorMessage.includes('jwt expired')) {
              wsAuthService.clearToken();
            }
          };

          let lastAuthSuccessAt = 0;
          const SESSION_REPLACED_GRACE_MS = 5_000;

          const handleAuthSuccess = (data: { user?: { id?: string; organizationId?: string } }) => {
            reduxLogger.info('[SocketProvider] Authentication successful:', data);
            lastAuthSuccessAt = Date.now();

            if (isSessionInvalidated()) {
              reduxLogger.info('[SocketProvider] Clearing stale session invalidation after AUTH_SUCCESS');
              resetSessionInvalidated();
              socketClient.sessionInvalidated = false;
            }

            const userId = data.user?.id || apiUserId;
            const orgId = data.user?.organizationId || user?.organizationId;
            if (userId && orgId) {
              const userIdStr = String(userId);
              const orgIdStr = String(orgId);
              socketClient.setUserContext(userIdStr, orgIdStr);
              socketClient.joinOrganizationRoom(orgIdStr);
              try { socketInstance.emit(ClientEvents.JOIN_ROOM, { room: `organization:${orgIdStr}` }); } catch {}
              currentOrgIdRef.current = orgIdStr;
              reduxLogger.info('[SocketProvider] Joined org room:', `org:${orgIdStr}`);
            }
          };

          const handleRoomJoined = (room: string) => {
            reduxLogger.info('[SocketProvider] Joined room:', room);
          };
          const handleRoomLeft = (room: string) => {
            reduxLogger.info('[SocketProvider] Left room:', room);
          };

          const handleServerBusy = (rawData: unknown) => {
            const data = (typeof rawData === 'object' && rawData !== null ? rawData : {}) as { retryAfterMs?: number };
            const retryAfterMs = typeof data.retryAfterMs === 'number' && data.retryAfterMs > 0
              ? data.retryAfterMs
              : 30_000;
            reduxLogger.warn('[SocketProvider] Server busy (reconnect storm) — adding backoff', { retryAfterMs });
            socketClient.setServerBusyUntil(Date.now() + retryAfterMs);
          };

          registerHandler(socketInstance, 'connect', handleConnect);
          registerHandler(socketInstance, 'disconnect', handleDisconnect);
          registerHandler(socketInstance, 'connect_error', handleConnectError);
          registerHandler(socketInstance, 'reconnect', handleReconnect as (...args: unknown[]) => void);
          registerHandler(socketInstance, ServerEvents.ERROR, handleError);
          registerHandler(socketInstance, ServerEvents.SERVER_BUSY, handleServerBusy);
          registerHandler(socketInstance, ServerEvents.AUTH_SUCCESS, handleAuthSuccess);
          registerHandler(socketInstance, ServerEvents.ROOM_JOINED, handleRoomJoined);
          registerHandler(socketInstance, ServerEvents.ROOM_LEFT, handleRoomLeft);

          const handleSessionReplaced = (data: { clientType?: string; timestamp?: number }) => {
            if (data?.clientType && data.clientType !== "web") return;
            const timeSinceAuth = Date.now() - lastAuthSuccessAt;
            if (lastAuthSuccessAt > 0 && timeSinceAuth < SESSION_REPLACED_GRACE_MS) {
              reduxLogger.info('[SocketProvider] SESSION_REPLACED ignored — within auth grace period', { timeSinceAuth });
              return;
            }
            reduxLogger.warn('[SocketProvider] SESSION_REPLACED — signed in from another location');
            markSessionInvalidated();
            socketClient.sessionInvalidated = true;
            window.dispatchEvent(
              new CustomEvent('auth:session_replaced', {
                detail: { reason: 'signed_in_elsewhere', timestamp: Date.now() },
              })
            );
          };
          registerHandler(socketInstance, ServerEvents.SESSION_REPLACED, handleSessionReplaced);

          return socketInstance;
        } else {
          socketLogger.error('[SocketProvider] Failed to establish socket connection');
          return null;
        }
      } catch (error) {
        const errorMessage = getErrorMessage(error);
        socketLogger.error('[SocketProvider] Error connecting to socket:', errorMessage);
        return null;
      } finally {
        if (isMountedRef.current) setConnecting(false);
        connectionPromiseRef.current = null;
      }
    })();

    connectionPromiseRef.current = connectionPromise;
    return connectionPromise;
  }, [isAuthenticated, socketClient, apiUserId, user?.organizationId, registerHandler]);

  useEffect(() => {
    connectSocketRef.current = connectSocket;
  }, [connectSocket]);

  // Register reconnect callback with SocketClient
  useEffect(() => {
    const handleReconnectRequest = async (reason: string): Promise<void> => {
      socketLogger.info('[SocketProvider] Reconnect requested via callback:', reason);
      connectionVersionRef.current++;
      connectionPromiseRef.current = null;
      if (connectSocketRef.current) await connectSocketRef.current();
    };
    socketClient.setReconnectCallback(handleReconnectRequest);
    socketLogger.info('[SocketProvider] Registered reconnect callback with SocketClient');
    return () => { socketClient.setReconnectCallback(null); };
  }, [socketClient]);

  const disconnectSocket = useCallback(() => {
    socketLogger.info('[SocketProvider] Disconnecting socket...');
    connectionVersionRef.current++;
    connectionPromiseRef.current = null;
    if (currentOrgIdRef.current) {
      socketClient.leaveOrganizationRoom(currentOrgIdRef.current);
      currentOrgIdRef.current = null;
    }
    cleanupAllHandlers(socketRef.current);
    socketClient.disconnect();
    if (isMountedRef.current) {
      setSocket(null);
      setConnected(false);
      setConnecting(false);
    }
    store.dispatch({ type: 'socket/disconnected' });
  }, [socketClient, cleanupAllHandlers]);

  // Connect as soon as authenticated
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const orgId = user?.organizationId;
    const newAuthContext: AuthContext = {
      isAuthenticated,
      orgId: typeof orgId === 'string' ? orgId : null
    };
    const prevAuthContext = currentAuthContextRef.current;
    const authChanged = prevAuthContext.isAuthenticated !== newAuthContext.isAuthenticated;
    const orgChanged = prevAuthContext.orgId !== newAuthContext.orgId &&
                       prevAuthContext.isAuthenticated && newAuthContext.isAuthenticated;
    currentAuthContextRef.current = newAuthContext;

    if (newAuthContext.isAuthenticated) {
      if (authChanged || orgChanged) {
        if (socketRef.current?.connected || connectionPromiseRef.current) {
          socketLogger.info('[SocketProvider] Auth/org context changed mid-connect, restarting connection');
          disconnectSocket();
        }
      }
      connectSocket();
    } else {
      disconnectSocket();
    }
  }, [isAuthenticated, user?.organizationId, connectSocket, disconnectSocket]);

  // Cleanup on unmount
  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    return () => {
      socketLogger.info('[SocketProvider] Unmounting - cleaning up socket handlers');
      connectionVersionRef.current++;
      connectionPromiseRef.current = null;
      cleanupAllHandlers(socketRef.current);
      if (currentOrgIdRef.current) {
        socketClient.leaveOrganizationRoom(currentOrgIdRef.current);
        currentOrgIdRef.current = null;
      }
      socketClient.disconnect();
    };
  }, [socketClient, cleanupAllHandlers]);
  /* eslint-enable react-hooks/exhaustive-deps */

  // Tab visibility recovery
  useEffect(() => {
    return attachWindowAttentionListeners(async () => {
      if (!isSessionInvalidated()) return;
      socketLogger.info('[SocketProvider] Tab visible — session was invalidated, attempting recovery');
      try {
        const didRecover = await recoverSessionState({ force: true, requireInvalidated: true });
        if (!didRecover) {
          socketLogger.info('[SocketProvider] Session truly invalid — leaving invalidated');
          return;
        }
        socketLogger.info('[SocketProvider] Session still valid — resetting invalidation flags');
        connectionVersionRef.current++;
        connectionPromiseRef.current = null;
        if (connectSocketRef.current) await connectSocketRef.current();
        socketLogger.info('[SocketProvider] Recovery complete — socket reconnected');
      } catch (err) {
        socketLogger.error('[SocketProvider] Tab visibility recovery failed:', err);
      }
    }, { onVisible: true, onFocus: false, onOnline: false, minIntervalMs: 250 });
  }, [socketClient]);

  // Update user context when user data changes
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (connected && socket && apiUserId && user?.organizationId) {
      socketClient.setUserContext(String(apiUserId), String(user.organizationId));
      if (currentOrgIdRef.current !== String(user.organizationId)) {
        if (currentOrgIdRef.current) {
          socketClient.leaveOrganizationRoom(String(currentOrgIdRef.current));
        }
        socketClient.joinOrganizationRoom(String(user.organizationId));
        currentOrgIdRef.current = String(user.organizationId);
      }
    }
  }, [connected, socket, apiUserId, user?.organizationId, socketClient]);

  return {
    socket,
    connected,
    connecting,
    socketStable,
    socketClient,
    registerHandler,
  };
}
